import type { Icon } from '@phosphor-icons/react';
import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * What an empty list says: one title, at most one line, at most one action. Compact on purpose; it
 * sits inside the panel or sheet that would hold the rows, never as a page-sized card of its own.
 * The action here is the only copy of it on screen while the list is empty (docs/DESIGN.md §6).
 */
export function EmptyState({
  icon: Glyph,
  title,
  children,
  action,
  className,
}: {
  icon?: Icon;
  title: ReactNode;
  /** One short line: what will appear here, or how to make it appear. */
  children?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-col items-center px-6 py-10 text-center', className)}>
      {Glyph && <Glyph aria-hidden="true" className="mb-3 size-5 text-subtle" />}
      <p className="type-section text-foreground">{title}</p>
      {children !== undefined && (
        <p className="mt-1 max-w-[40ch] text-[0.8125rem] text-muted-foreground">{children}</p>
      )}
      {action !== undefined && <div className="mt-4 flex items-center gap-2">{action}</div>}
    </div>
  );
}
